import {
  computed,
  onBeforeUnmount,
  onMounted,
  readonly,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from "vue";
import {
  advanceTimerState,
  calculateTimerSnapshot,
  createInitialTimerState,
  createTimerConfigFingerprint,
  normalizeTimerConfig,
  pauseTimerState,
  restoreTimerState,
  resumeTimerState,
  type TimerAnswerState,
  type TimerCompletionStatus,
  type TimerConfig,
  type TimerInitialStatus,
  type TimerMode,
  type TimerPersistenceMode,
} from "#bb/lib/timer";

export interface UsePersistentTimerOptions {
  id: MaybeRefOrGetter<string>;
  mode: MaybeRefOrGetter<TimerMode>;
  durationMs: MaybeRefOrGetter<number | null | undefined>;
  persistenceMode: MaybeRefOrGetter<TimerPersistenceMode>;
  initialStatus?: MaybeRefOrGetter<TimerInitialStatus>;
  editor?: MaybeRefOrGetter<boolean>;
  tickIntervalMs?: number;
  persistIntervalMs?: number;
  onError?: (error: unknown) => void;
}

export function usePersistentTimer(options: UsePersistentTimerOptions) {
  const storage = useTimerStateStorage();
  const nowMs = shallowRef(0);
  const initialized = shallowRef(false);
  const persisting = shallowRef(false);
  const lastError = shallowRef<unknown>(null);
  const lastSuccessfulPersistenceAt = shallowRef<number | null>(null);
  const tickIntervalMs = Math.max(50, options.tickIntervalMs ?? 250);
  const persistIntervalMs = Math.max(250, options.persistIntervalMs ?? 5000);
  let ticker: ReturnType<typeof setInterval> | null = null;
  let persistenceQueue: Promise<unknown> = Promise.resolve();
  let nextThrottledPersistenceAt = 0;
  let disposed = false;

  const editor = computed(() => Boolean(toValue(options.editor)));
  const config = computed<TimerConfig>(() =>
    normalizeTimerConfig({
      mode: toValue(options.mode),
      durationMs: toValue(options.durationMs) ?? 0,
      persistenceMode: toValue(options.persistenceMode),
      initialStatus: options.initialStatus
        ? toValue(options.initialStatus)
        : "running",
    }),
  );
  const configFingerprint = computed(() =>
    createTimerConfigFingerprint(config.value),
  );
  const state = shallowRef<TimerAnswerState>(
    createInitialTimerState(config.value, 0),
  );
  const snapshot = computed(() =>
    calculateTimerSnapshot(state.value, config.value, nowMs.value),
  );
  const shouldExecuteCompletion = computed(
    () =>
      !editor.value &&
      initialized.value &&
      snapshot.value.completed &&
      state.value.completionStatus === "pending",
  );

  function reportError(error: unknown) {
    lastError.value = error;
    if (options.onError) {
      try {
        options.onError(error);
      } catch (handlerError) {
        console.error("Persistent timer error handler failed:", handlerError);
      }
      return;
    }
    console.error("Persistent timer error:", error);
  }

  function stopTicker() {
    if (ticker !== null) {
      clearInterval(ticker);
      ticker = null;
    }
  }

  function tick() {
    if (!initialized.value || editor.value || state.value.status !== "running") {
      return;
    }

    const currentTime = Date.now();
    nowMs.value = currentTime;
    state.value = advanceTimerState(state.value, config.value, currentTime);

    if (state.value.status === "completed") {
      stopTicker();
      void persist(true).catch(() => undefined);
      return;
    }

    void persist(false).catch(() => undefined);
  }

  function ensureTicker() {
    stopTicker();
    if (
      import.meta.client &&
      !disposed &&
      initialized.value &&
      !editor.value &&
      state.value.status === "running"
    ) {
      ticker = setInterval(tick, tickIntervalMs);
    }
  }

  function persist(force = false): Promise<boolean> {
    if (!initialized.value || editor.value) return Promise.resolve(false);

    const scheduledAt = Date.now();
    if (!force && scheduledAt < nextThrottledPersistenceAt) {
      return Promise.resolve(false);
    }
    if (!force) {
      nextThrottledPersistenceAt = scheduledAt + persistIntervalMs;
    }

    const operation = persistenceQueue.then(async () => {
      persisting.value = true;
      try {
        const blockId = toValue(options.id);
        if (!blockId) {
          throw new Error("A block id is required to persist timer state.");
        }

        await storage.save(blockId, state.value);
        lastSuccessfulPersistenceAt.value = Date.now();
        lastError.value = null;
        return true;
      } catch (error) {
        reportError(error);
        throw error;
      } finally {
        persisting.value = false;
      }
    });

    persistenceQueue = operation.catch(() => undefined);
    return operation;
  }

  async function pause() {
    if (!initialized.value || editor.value) return;
    const currentTime = Date.now();
    nowMs.value = currentTime;
    const previous = state.value;
    state.value = pauseTimerState(previous, config.value, currentTime);
    stopTicker();
    try {
      await persist(true);
    } catch (error) {
      state.value = previous;
      ensureTicker();
      throw error;
    }
  }

  async function resume() {
    if (!initialized.value || editor.value || state.value.status === "completed") {
      return;
    }
    const currentTime = Date.now();
    nowMs.value = currentTime;
    const previous = state.value;
    state.value = resumeTimerState(previous, config.value, currentTime);
    try {
      await persist(true);
    } catch (error) {
      state.value = previous;
      throw error;
    }
    ensureTicker();
  }

  async function reset() {
    if (!initialized.value || editor.value) return;
    const currentTime = Date.now();
    nowMs.value = currentTime;
    state.value = createInitialTimerState(config.value, currentTime);
    await persist(true);
    ensureTicker();
  }

  async function setCompletionStatus(status: TimerCompletionStatus) {
    const previous = state.value;
    state.value = { ...previous, completionStatus: status };
    try {
      await persist(true);
    } catch (error) {
      state.value = previous;
      throw error;
    }
  }

  async function claimCompletion(): Promise<boolean> {
    if (!shouldExecuteCompletion.value) return false;
    await setCompletionStatus("executing");
    return true;
  }

  async function markCompletionExecuted() {
    if (
      state.value.completionStatus !== "executing" &&
      state.value.completionStatus !== "pending"
    ) {
      return;
    }
    await setCompletionStatus("executed");
  }

  async function releaseCompletionClaim() {
    if (state.value.completionStatus !== "executing") return;
    await setCompletionStatus("pending");
  }

  watch(configFingerprint, () => {
    if (editor.value) {
      state.value = createInitialTimerState(config.value, 0);
      nowMs.value = 0;
      return;
    }
    if (!initialized.value) return;

    const currentTime = Date.now();
    nowMs.value = currentTime;
    state.value = createInitialTimerState(config.value, currentTime);
    ensureTicker();
    void persist(true).catch(() => undefined);
  });

  onMounted(async () => {
    if (editor.value || !import.meta.client) return;

    try {
      const blockId = toValue(options.id);
      if (!blockId) {
        throw new Error("A block id is required to restore timer state.");
      }

      const currentTime = Date.now();
      const persistedState = await storage.load(blockId);
      if (disposed) return;

      nowMs.value = currentTime;
      state.value = restoreTimerState(
        persistedState,
        config.value,
        currentTime,
      );
      initialized.value = true;
      await persist(true);
    } catch (error) {
      if (lastError.value !== error) {
        reportError(error);
      }
    } finally {
      ensureTicker();
    }
  });

  onBeforeUnmount(() => {
    disposed = true;
    stopTicker();
    if (!initialized.value || editor.value) return;

    const currentTime = Date.now();
    nowMs.value = currentTime;
    state.value = advanceTimerState(state.value, config.value, currentTime);
    void persist(true).catch(() => undefined);
  });

  return {
    state: readonly(state),
    snapshot,
    initialized: readonly(initialized),
    persisting: readonly(persisting),
    lastError: readonly(lastError),
    lastSuccessfulPersistenceAt: readonly(lastSuccessfulPersistenceAt),
    shouldExecuteCompletion,
    pause,
    resume,
    reset,
    persist,
    claimCompletion,
    markCompletionExecuted,
    releaseCompletionClaim,
  };
}
